import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

/**
 * OfflineIndicator
 *
 * Shows a fixed red banner at the top of the screen whenever the device
 * loses its internet connection, and a toast when it comes back.
 *
 * Uses the browser's native `offline` / `online` events (navigator.onLine)
 * — no polling, works on desktop and mobile browsers.
 *
 * Mounted once in Layout.jsx so it appears on every admin panel page.
 */
const OfflineIndicator = () => {
  const [isOffline, setIsOffline] = useState(
    typeof navigator !== 'undefined' ? !navigator.onLine : false
  );

  useEffect(() => {
    const goOffline = () => {
      setIsOffline(true);
      toast.error('No internet connection — you are offline', {
        id: 'offline-toast', // dedupes if fired repeatedly
        duration: 5000,
      });
    };
    const goOnline = () => {
      setIsOffline(false);
      toast.success('Back online', {
        id: 'online',
        duration: 3000,
      });
    };

    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, []);

  if (!isOffline) return null;

  return (
    <div
      role="alert"
      className="fixed top-0 left-0 right-0 z-[100] bg-red-600 text-white text-sm font-medium text-center py-2 px-4 shadow-lg"
    >
      <span className="inline-flex items-center gap-2">
        {/* Pulsing dot — signals "waiting for connection" */}
        <span className="w-2 h-2 rounded-full bg-white animate-pulse flex-shrink-0" />
        No internet connection — data may not load or save until you are back online
      </span>
    </div>
  );
};

export default OfflineIndicator;